import React from 'react';

import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import styles from './ReviewBulkAssignmentModal.module.scss';

export type SkipBucket = 'not_found' | 'inactive' | 'conflict';

export interface SkippedRow {
  carrierPolicyNumber: string;
  bucket: SkipBucket;
  reasonLabel: string;
  reasonDetail: string;
  conflictAssignedTo?: string;
}

export interface ReadyRow {
  carrierPolicyNumber: string;
  applicantName?: string;
}

export interface Assignee {
  id: number;
  name: string;
  initials: string;
}

export interface ValidationResponse {
  ready: ReadyRow[];
  skipped: SkippedRow[];
  assignee: Assignee;
}

interface Props {
  open: boolean;
  validation: ValidationResponse;
  assignmentTypeLabel: string;
  isProceeding: boolean;
  onCancel: () => void;
  onProceed: (includeConflicts: boolean) => void;
}

const BUCKET_BADGE_LABEL: Record<SkipBucket, string> = {
  not_found: 'Not found',
  inactive: 'Inactive',
  conflict: 'Conflict',
};

/** Concatenate truthy class names with a single space (mini-clsx). */
function cn(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

export function ReviewBulkAssignmentModal({
  open,
  validation,
  assignmentTypeLabel,
  isProceeding,
  onCancel,
  onProceed,
}: Props) {
  const readyCount = validation.ready.length;
  const conflictRows = validation.skipped.filter(s => s.bucket === 'conflict');
  const conflictCount = conflictRows.length;
  const hardSkipCount = validation.skipped.filter(
    s => s.bucket !== 'conflict',
  ).length;

  return (
    <Dialog
      open={open}
      onClose={isProceeding ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxWidth: 720 } }}
    >
      <DialogTitle sx={{ padding: '10px 16px 6px' }}>
        <Typography component="div" className={styles.title}>
          Review bulk assignment
        </Typography>
        <Typography component="div" className={styles.subtitle}>
          Assigning to {validation.assignee.name} · {assignmentTypeLabel}
        </Typography>
      </DialogTitle>

      {(hardSkipCount > 0 || conflictCount > 0) && (
        <Box className={styles.warningBar}>
          <Box className={styles.warningIcon}>!</Box>
          <Typography variant="caption" className={styles.warningText}>
            {`${hardSkipCount + conflictCount} policy ID${hardSkipCount + conflictCount === 1 ? '' : 's'} ha${hardSkipCount + conflictCount === 1 ? 's' : 've'} issues.`}
            {hardSkipCount > 0 &&
              ` ${hardSkipCount} will always be skipped (${conflictCount > 0 ? 'not found / inactive' : 'not found or inactive'}).`}
            {conflictCount > 0 &&
              ` ${conflictCount} ha${conflictCount === 1 ? 's' : 've'} an assignment conflict and can be overridden — see options below.`}
          </Typography>
        </Box>
      )}

      <Box className={styles.columnHeaders}>
        <Typography variant="caption" className={styles.columnHeaderText}>
          Policy ID
        </Typography>
        <Typography variant="caption" className={styles.columnHeaderText}>
          Details
        </Typography>
        <Typography
          variant="caption"
          className={styles.columnHeaderText}
          sx={{ textAlign: 'right' }}
        >
          Status
        </Typography>
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: 340 }}>
        {/* Skipped rows render first so issues are visible without scrolling. */}
        {validation.skipped.map((row, idx) => {
          const isLastRow =
            idx === validation.skipped.length - 1 &&
            validation.ready.length === 0;
          return (
            <Box
              key={`skipped-${row.carrierPolicyNumber}-${idx}`}
              className={cn(
                styles.row,
                row.bucket === 'conflict'
                  ? styles.rowConflict
                  : styles.rowSkipped,
                isLastRow && styles.rowLast,
              )}
            >
              <Typography variant="body2" className={styles.policyId}>
                {row.carrierPolicyNumber}
              </Typography>
              <Box>
                <Typography
                  className={
                    row.bucket === 'conflict'
                      ? styles.conflictLabel
                      : styles.skippedLabel
                  }
                >
                  {row.reasonLabel}
                </Typography>
                <Typography
                  variant="caption"
                  className={
                    row.bucket === 'conflict'
                      ? styles.conflictDetail
                      : styles.skippedDetail
                  }
                >
                  {row.reasonDetail}
                </Typography>
              </Box>
              <Chip
                label={BUCKET_BADGE_LABEL[row.bucket]}
                className={cn(
                  styles.badge,
                  row.bucket === 'conflict'
                    ? styles.badgeConflict
                    : styles.badgeSkipped,
                )}
              />
            </Box>
          );
        })}

        {validation.ready.map((row, idx) => {
          const isLastRow = idx === validation.ready.length - 1;
          return (
            <Box
              key={`ready-${row.carrierPolicyNumber}`}
              className={cn(
                styles.row,
                styles.rowReady,
                isLastRow && styles.rowLast,
              )}
            >
              <Typography variant="body2" className={styles.policyId}>
                {row.carrierPolicyNumber}
              </Typography>
              <Box className={styles.assigneeCell}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '11px',
                    fontWeight: 500,
                    bgcolor: '#e3f2fd',
                    color: '#1565c0',
                  }}
                >
                  {validation.assignee.initials}
                </Avatar>
                <Typography className={styles.assigneeName}>
                  {validation.assignee.name}
                </Typography>
              </Box>
              <Chip
                label="Ready"
                className={cn(styles.badge, styles.badgeReady)}
              />
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions
        className={styles.footer}
        sx={{ flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box className={styles.footerMain}>
          <Typography variant="caption" className={styles.footerNote}>
            {hardSkipCount > 0 &&
              `${hardSkipCount} always skipped (${conflictCount > 0 ? 'not found / inactive' : 'not found or inactive'}).`}
            {hardSkipCount > 0 && conflictCount > 0 && ' '}
            {conflictCount > 0 &&
              `${conflictCount} conflict${conflictCount === 1 ? '' : 's'} can be overridden.`}
          </Typography>
          <Box className={styles.footerButtons}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={onCancel}
              disabled={isProceeding}
              className={styles.cancelButton}
            >
              Cancel
            </Button>
            {conflictCount > 0 ? (
              <>
                {readyCount > 0 && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => onProceed(false)}
                    disabled={isProceeding}
                    className={styles.assignButtonOutline}
                  >
                    {`Assign ${readyCount} without conflict`}
                  </Button>
                )}
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => onProceed(true)}
                  disabled={isProceeding}
                  startIcon={
                    isProceeding ? <CircularProgress size={16} /> : null
                  }
                  className={styles.assignButton}
                  sx={{
                    bgcolor: '#111',
                    color: '#fff',
                    '&:hover': { bgcolor: '#111', opacity: 0.85 },
                    '&.Mui-disabled': {
                      bgcolor: '#111',
                      opacity: 0.4,
                      color: '#fff',
                    },
                  }}
                >
                  {isProceeding
                    ? 'Assigning...'
                    : `Assign ${readyCount + conflictCount} including conflict`}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                disableElevation
                onClick={() => onProceed(false)}
                disabled={isProceeding || readyCount === 0}
                startIcon={isProceeding ? <CircularProgress size={16} /> : null}
                className={styles.assignButton}
                sx={{
                  bgcolor: '#111',
                  color: '#fff',
                  '&:hover': { bgcolor: '#111', opacity: 0.85 },
                  '&.Mui-disabled': {
                    bgcolor: '#111',
                    opacity: 0.4,
                    color: '#fff',
                  },
                }}
              >
                {isProceeding
                  ? 'Assigning...'
                  : `Assign ${readyCount} case${readyCount === 1 ? '' : 's'}`}
              </Button>
            )}
          </Box>
        </Box>
        {conflictCount > 0 && (
          <Typography
            variant="caption"
            className={styles.footerConflictNote}
            display="block"
          >
            {`⚠ ${
              conflictCount === 1
                ? `"Assign including conflict" will reassign case ${conflictRows[0].carrierPolicyNumber} away from ${conflictRows[0].conflictAssignedTo}.`
                : `"Assign including conflict" will reassign ${conflictCount} cases currently assigned to other users.`
            }`}
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
}
